import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Typography,
  Avatar,
  Box,
  Alert,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Close, Devices, Router, Cable, Search, Clear } from '@mui/icons-material';
import equipmentService from '../../services/equipmentService';
import { contractService } from '../../services/contractService';
import EquipmentForm from '../forms/EquipmentForm';
import ContractServicosSelect from '../selects/ContractServicosSelect';

interface Equipment {
  id: number;
  modeloId: number;
  numero_serie: string;
  mac_address?: string;
  gpon_sn?: string;
  modalidade: 'proprio' | 'comodato';
  status_uso: 'livre' | 'em_uso';

  end_estoque?: string;
  modelo?: {
    id: number;
    titulo: string;
    tipo: 'ONU' | 'ONT' | 'Roteador' | 'Switch';
    marca?: {
      id: number;
      titulo: string;
    };
  };
}

interface AddEquipamentoModalProps {
  open: boolean;
  onClose: () => void;
  onEquipmentAdded: () => void;
  contractId: number;
}

const AddEquipamentoModal: React.FC<AddEquipamentoModalProps> = ({
  open,
  onClose,
  onEquipmentAdded,
  contractId,
}) => {
  const [availableEquipments, setAvailableEquipments] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [equipmentFormOpen, setEquipmentFormOpen] = useState(false);
  const [selectedServicoId, setSelectedServicoId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [equipmentSearch, setEquipmentSearch] = useState('');

  useEffect(() => {
    if (open) {
      setAvailableEquipments([]);
      setError(null);
      setSelectedServicoId(null);
      setEquipmentSearch('');
      loadContractData();
      loadAvailableEquipments();
    }
  }, [open, contractId]);

  const loadContractData = async () => {
    try {
      const response = await contractService.getById(contractId);
      if (response.success && response.data) {
        setClientId(response.data.cliente_id);
      }
    } catch (err: any) {
      console.error('Erro ao carregar dados do contrato:', err);
    }
  };

  const loadAvailableEquipments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await equipmentService.getEquipments({
        status_uso: 'livre',
        limit: 100
      });
      let equipmentsData: Equipment[] = [];
      if (response && response.success && response.data && (response.data as any).equipments && Array.isArray((response.data as any).equipments)) {
        equipmentsData = (response.data as any).equipments;
      } else if (response && response.success && response.data && Array.isArray(response.data)) {
        equipmentsData = response.data;
      } else if (response && (response as any).equipments && Array.isArray((response as any).equipments)) {
        equipmentsData = (response as any).equipments;
      } else if (response && Array.isArray(response)) {
        equipmentsData = response;
      } else if (response && response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        const dataKeys = Object.keys(response.data);
        for (const key of dataKeys) {
          const value = (response.data as any)[key];
          if (Array.isArray(value)) {
            equipmentsData = value;
            break;
          }
        }
      } else {
        setError('Formato de dados inesperado da API de equipamentos');
        return;
      }
      // Filtrar apenas equipamentos Roteador e Switch
      const filtered = equipmentsData.filter((equipment: Equipment) => {
        const tipo = equipment.modelo?.tipo?.toLowerCase();
        return tipo === 'roteador' || tipo === 'switch';
      });
      setAvailableEquipments(filtered);
    } catch (err: any) {
      setError(`Erro ao carregar equipamentos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentSelect = async (equipment: Equipment) => {
    // Validar se serviço foi selecionado
    if (!selectedServicoId) {
      setError('Selecione um serviço antes de vincular o equipamento');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Vincular equipamento ao contrato
      const updateData: any = {

        contrato_id: contractId,
        contrato_servico_id: selectedServicoId
      };

      await equipmentService.updateEquipment(equipment.id, updateData);
      onEquipmentAdded();
      onClose();
    } catch (err: any) {
      setError('Erro ao vincular equipamento ao contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleServicoChange = (servicoId: number | null, servico?: any, contractId?: number) => {
    setSelectedServicoId(servicoId);
  };

  const handleEquipmentFormSuccess = () => {
    setEquipmentFormOpen(false);
    loadAvailableEquipments();
  };

  const getEquipmentTypeIcon = (tipo?: string) => {
    switch (tipo?.toLowerCase()) {
      case 'roteador':
        return <Router />;
      case 'switch':
        return <Cable />;
      default:
        return <Devices />;
    }
  };

  const filteredEquipments = availableEquipments.filter((equipment) => {
    if (!equipmentSearch.trim()) return true;
    const query = equipmentSearch.toLowerCase();
    const modeloTitulo = equipment.modelo?.titulo?.toLowerCase() || '';
    const modeloTipo = equipment.modelo?.tipo?.toLowerCase() || '';
    const marcaTitulo = equipment.modelo?.marca?.titulo?.toLowerCase() || '';
    const numeroSerie = equipment.numero_serie?.toLowerCase() || '';
    const macAddress = equipment.mac_address?.toLowerCase() || '';
    const gponSn = equipment.gpon_sn?.toLowerCase() || '';
    const endEstoque = equipment.end_estoque?.toLowerCase() || '';

    return (
      modeloTitulo.includes(query) ||
      modeloTipo.includes(query) ||
      marcaTitulo.includes(query) ||
      numeroSerie.includes(query) ||
      macAddress.includes(query) ||
      gponSn.includes(query) ||
      endEstoque.includes(query)
    );
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 1.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
            Adicionar Equipamento ao Contrato
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ p: 0.5 }}>
            <Close sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 1.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
            {error}
          </Alert>
        )}
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}

        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid item xs={12}>
            <ContractServicosSelect
              value={selectedServicoId}
              onChange={handleServicoChange}
              contractId={contractId}
              label="Serviço"
              placeholder="Selecione um serviço para vincular o equipamento"
              required
              disabled={loading}
              size="small"
              onlyActive={true}
              showContractTitle={false}
              allowClear={false}
              error={!!error && !selectedServicoId}
              helperText={!selectedServicoId ? 'Selecione um serviço para continuar' : ''}
            />
          </Grid>
        </Grid>

        <Typography variant="body1" sx={{ fontSize: '0.9rem', mb: 1 }}>
          Selecione um equipamento do tipo <b>Roteador</b> ou <b>Switch</b> para vincular ao contrato.
        </Typography>

        <Grid container spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Pesquisar por modelo, série, MAC..."
              value={equipmentSearch}
              onChange={(e) => setEquipmentSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: '1rem' }} />
                  </InputAdornment>
                ),
                endAdornment: equipmentSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setEquipmentSearch('')}>
                      <Clear sx={{ fontSize: '0.9rem' }} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={() => setEquipmentFormOpen(true)}
              disabled={loading}
              size="small"
              sx={{ fontSize: '0.8rem', py: 0.75, height: 40, whiteSpace: 'nowrap' }}
            >
              Cadastrar Equipamento
            </Button>
          </Grid>
        </Grid>
        <EquipmentForm
          open={equipmentFormOpen}
          onClose={() => setEquipmentFormOpen(false)}
          onSuccess={handleEquipmentFormSuccess}
        />
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Tipo</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Modelo</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Série</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>MAC Address</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Modalidade</TableCell>
                <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Localização</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEquipments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                      {availableEquipments.length === 0
                        ? 'Nenhum equipamento Roteador/Switch disponível'
                        : 'Nenhum equipamento encontrado para a pesquisa'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredEquipments.map((equipment) => (
                  <TableRow
                    key={equipment.id}
                    hover
                    sx={{ cursor: selectedServicoId ? 'pointer' : 'not-allowed', opacity: selectedServicoId ? 1 : 0.5 }}
                    onClick={() => selectedServicoId && handleEquipmentSelect(equipment)}
                  >
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                          {getEquipmentTypeIcon(equipment.modelo?.tipo)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {equipment.modelo?.tipo || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        {equipment.modelo?.titulo || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {equipment.modelo?.marca?.titulo || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {equipment.numero_serie}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {equipment.mac_address || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Chip
                        label={equipment.modalidade === 'proprio' ? 'Próprio' : 'Comodato'}
                        color={equipment.modalidade === 'proprio' ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1.5 }}>
                      <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.8rem' }}>
                        {equipment.end_estoque || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEquipamentoModal; 